<!--
  TopUpModal — category-correction flow.
  Replaces the old time-extension top-up. Used by:
    - SBP Enforcement (BusinessEnforcement.vue) when an officer finds a
      business invoiced under the wrong SBP category.
    - SBP Approved (Approved.vue) — supervisors can also correct categories.

  Flow:
    1. Show current business + current bill amount (already paid).
    2. Officer picks the correct category and sub-category. Fee is auto-pulled.
    3. Difference is computed (newFee - currentBillAmount). Negative differences
       are shown as 0 (we don't refund overpayments via this flow).
    4. Officer can add additional charges (penalties, etc.) as line items.
    5. Total = max(0, difference) + sum(extras).
    6. Generate invoice via existing generateBill3.
    7. Record the pending category change (recordCategoryChange) — the
       countyTrade business record only flips category once the bill is paid.
    8. Tag in enforcement_invoices (recordEnforcementInvoice).

  Emits:
    @success(result) — { billNo, amount, businessID } so callers can react
                       (BusinessEnforcement uses it to call its own enforcement
                       tagging hook; Approved.vue uses it to refresh the list).
-->
<template>
  <div class="modal fade" id="topUpModal" tabindex="-1" aria-labelledby="topUpModalLabel" aria-hidden="true" data-bs-backdrop="static">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-fullscreen-sm-down">
      <div class="modal-content">
        <div class="modal-header bg-warning text-dark">
          <h5 class="modal-title">
            <i class="bx bx-edit-alt me-2"></i>Correct Business Category — Top Up Invoice
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" :disabled="processing"></button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-warning"></div>
            <p class="mt-3 mb-0">Loading…</p>
          </div>

          <div v-else>

            <!-- Current state -->
            <div class="card border mb-3">
              <div class="card-header bg-light py-2"><strong>Current Status</strong></div>
              <div class="card-body p-3">
                <div class="row g-2">
                  <div class="col-md-6"><small class="text-muted">Business</small><div class="fw-bold">{{ businessName || '—' }}</div></div>
                  <div class="col-md-6"><small class="text-muted">Business ID</small><div>{{ businessID || '—' }}</div></div>
                  <div class="col-md-6"><small class="text-muted">Current Category</small><div>{{ oldCategoryName || '—' }}</div></div>
                  <div class="col-md-6"><small class="text-muted">Current Sub-Category</small><div>{{ oldSubCategoryName || '—' }}</div></div>
                  <div class="col-md-6"><small class="text-muted">Current Bill</small><div><code>{{ billNo || '—' }}</code></div></div>
                  <div class="col-md-6"><small class="text-muted">Amount on Current Bill</small><div class="fw-bold">KES {{ formatMoney(currentBillAmount) }}</div></div>
                </div>
              </div>
            </div>

            <!-- New (correct) category -->
            <div class="card border mb-3">
              <div class="card-header bg-light py-2"><strong>Correct Category</strong></div>
              <div class="card-body p-3">
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">Category <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="selectedCategory" @change="onCategorySelected">
                      <option :value="null">-- Select --</option>
                      <option v-for="c in categories" :key="c.incomeTypeId" :value="c">
                        {{ c.incomeTypeDescription }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">Sub-Category / Fee <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="selectedFee" :disabled="!selectedCategory || feesLoading">
                      <option :value="null">{{ feesLoading ? 'Loading...' : '-- Select --' }}</option>
                      <option v-for="f in fees" :key="f.feeId" :value="f">
                        {{ f.feeDescription }} — KES {{ formatMoney(f.unitFeeAmount) }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Quantity</label>
                    <input type="number" class="form-control" min="1" v-model.number="quantity">
                  </div>
                  <div class="col-md-8">
                    <label class="form-label">Reason for correction <span class="text-danger">*</span></label>
                    <input type="text" class="form-control" v-model="reason" placeholder="e.g. Premise size larger than declared, employee count wrong, etc.">
                  </div>
                </div>
              </div>
            </div>

            <!-- Difference summary -->
            <div v-if="selectedFee" class="card border mb-3">
              <div class="card-header bg-light py-2"><strong>Calculation</strong></div>
              <div class="card-body p-3">
                <table class="table table-sm mb-0">
                  <tbody>
                    <tr>
                      <td>Correct fee × {{ quantity }}</td>
                      <td class="text-end">KES {{ formatMoney(newFeeAmount) }}</td>
                    </tr>
                    <tr>
                      <td>Already on current bill</td>
                      <td class="text-end">- KES {{ formatMoney(currentBillAmount) }}</td>
                    </tr>
                    <tr class="table-active">
                      <td><strong>Difference (top-up)</strong></td>
                      <td class="text-end">
                        <strong>KES {{ formatMoney(difference) }}</strong>
                        <small v-if="rawDifference < 0" class="text-muted d-block">(no refund issued)</small>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- Additional charges -->
            <div class="card border mb-3">
              <div class="card-header bg-light py-2 d-flex justify-content-between align-items-center">
                <strong>Additional Charges</strong>
                <button class="btn btn-sm btn-outline-primary" type="button" @click="addExtraCharge">
                  <i class="bx bx-plus me-1"></i> Add Charge
                </button>
              </div>
              <div class="card-body p-3">
                <div v-if="!extras.length" class="text-muted text-center py-2">
                  No additional charges. Click "Add Charge" to include penalties or extra fees.
                </div>
                <div v-for="(line, i) in extras" :key="'x-' + i" class="row g-2 mb-2 align-items-end">
                  <div class="col-md-5">
                    <label class="form-label small mb-1" v-if="i === 0">Fee</label>
                    <select class="form-select form-select-sm" v-model="line.fee" @change="onExtraFeeChange(line)">
                      <option :value="null">
                        {{ extraFeeOptions.length ? '-- Pick a fee --' : 'Loading other charges...' }}
                      </option>
                      <option v-for="f in extraFeeOptions" :key="f.feeId" :value="f">
                        {{ f.feeDescription }}
                        <span v-if="f._incomeTypeDescription"> · {{ f._incomeTypeDescription }}</span>
                        <span v-else-if="f.incomeTypePrefix"> ({{ f.incomeTypePrefix }})</span>
                        — KES {{ formatMoney(f.unitFeeAmount) }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small mb-1" v-if="i === 0">Quantity</label>
                    <input type="number" min="1" class="form-control form-control-sm" v-model.number="line.quantity">
                  </div>
                  <div class="col-md-3">
                    <label class="form-label small mb-1" v-if="i === 0">Amount (KES)</label>
                    <input type="number" min="0" class="form-control form-control-sm" v-model.number="line.amount">
                  </div>
                  <div class="col-md-1 text-end">
                    <button class="btn btn-sm btn-outline-danger" type="button" @click="removeExtra(i)" title="Remove">
                      <i class="bx bx-x"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Totals -->
            <div class="card border-warning">
              <div class="card-body p-3">
                <div class="row text-center g-2">
                  <div class="col-4">
                    <small class="text-muted d-block">Difference</small>
                    <strong>KES {{ formatMoney(difference) }}</strong>
                  </div>
                  <div class="col-4">
                    <small class="text-muted d-block">Extras</small>
                    <strong>KES {{ formatMoney(extrasTotal) }}</strong>
                  </div>
                  <div class="col-4">
                    <small class="text-muted d-block">Top-Up Total</small>
                    <strong class="text-danger">KES {{ formatMoney(totalAmount) }}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="errorMessage" class="alert alert-danger mt-3 mb-0">
              <i class="bx bx-error-circle me-1"></i> {{ errorMessage }}
            </div>
            <div v-if="successMessage" class="alert alert-success mt-3 mb-0">
              <i class="bx bx-check-circle me-1"></i> {{ successMessage }}
              <div v-if="lastGeneratedBillNo" class="mt-2 d-flex flex-wrap gap-2">
                <button type="button" class="btn btn-success btn-sm" @click="printGeneratedBill">
                  <i class="bx bx-printer me-1"></i> Print / Share Invoice
                </button>
                <button type="button" class="btn btn-outline-secondary btn-sm" @click="closeAfterPrint" data-bs-dismiss="modal">
                  Close
                </button>
              </div>

              <!-- Send M-PESA payment prompt right from the success state -->
              <div v-if="lastGeneratedBillNo" class="mt-3 pt-3 border-top">
                <div class="fw-semibold mb-1">
                  <i class="bx bx-mobile-alt me-1"></i> Send Payment Prompt
                </div>
                <p class="text-muted small mb-2">
                  Send the customer an M-PESA STK push (or SMS link) to pay
                  <strong>KES {{ formatMoney(lastGeneratedBillAmount || totalAmount) }}</strong>
                  for invoice <code>{{ lastGeneratedBillNo }}</code>.
                  <span v-if="lastGeneratedBillAmount && lastGeneratedBillAmount !== totalAmount"
                        class="d-block text-warning mt-1">
                    <i class="bx bx-info-circle me-1"></i>
                    Amount differs from the calculated KES {{ formatMoney(totalAmount) }} — the
                    invoice was generated with additional charges (e.g. penalties).
                  </span>
                </p>
                <div class="row g-2 align-items-end">
                  <div class="col-12 col-md-6">
                    <label class="form-label small mb-1">Customer Phone</label>
                    <input type="tel" class="form-control"
                           placeholder="2547XXXXXXXX"
                           v-model.trim="prompt.phoneNumber">
                  </div>
                  <div class="col-12 col-md-6">
                    <button type="button" class="btn btn-success w-100"
                            @click="sendTopUpPaymentPrompt"
                            :disabled="prompt.sending || !prompt.phoneNumber">
                      <span v-if="prompt.sending">
                        <span class="spinner-border spinner-border-sm me-1"></span>Sending…
                      </span>
                      <span v-else>
                        <i class="bx bx-send me-1"></i> Send Payment Prompt
                      </span>
                    </button>
                  </div>
                </div>
                <div v-if="prompt.message"
                     class="alert mt-2 mb-0 py-2"
                     :class="prompt.success ? 'alert-success' : 'alert-warning'">
                  <i class="bx me-1" :class="prompt.success ? 'bx-check-circle' : 'bx-info-circle'"></i>
                  {{ prompt.message }}
                </div>

                <!-- Live polling status — listening for payment confirmation -->
                <div v-if="paymentCheck.active && !paymentCheck.receivedReceipt"
                     class="alert alert-info mt-2 mb-0 py-2 d-flex align-items-center">
                  <span class="spinner-border spinner-border-sm me-2"></span>
                  <div class="flex-grow-1">
                    Waiting for the customer to confirm payment…
                    <small class="text-muted d-block">
                      ({{ paymentCheck.secondsRemaining }}s left)
                    </small>
                  </div>
                  <button class="btn btn-sm btn-outline-secondary" @click="stopPaymentCheck">
                    Stop checking
                  </button>
                </div>

                <!-- Confirmed: payment landed -->
                <div v-if="paymentCheck.receivedReceipt"
                     class="alert alert-success mt-2 mb-0 py-2">
                  <i class="bx bx-check-circle me-1"></i>
                  <strong>Payment received!</strong>
                  <span v-if="paymentCheck.receivedReceipt.receiptNo">
                    Receipt: <code>{{ paymentCheck.receivedReceipt.receiptNo }}</code>
                  </span>
                  <small class="d-block text-muted mt-1">
                    The business category will now apply automatically.
                  </small>
                </div>

                <!-- Timed out — give officer a manual retry option -->
                <div v-if="paymentCheck.timedOut && !paymentCheck.receivedReceipt"
                     class="alert alert-warning mt-2 mb-0 py-2">
                  <i class="bx bx-time-five me-1"></i>
                  Didn't receive payment confirmation within
                  {{ Math.round(paymentTimeoutMs / 1000) }}s. The customer may
                  still complete the payment — check the invoice later. You can
                  also resend the prompt.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" :disabled="processing">Cancel</button>
          <button type="button" class="btn btn-warning" :disabled="!canSubmit || processing" @click="processTopUp">
            <span v-if="!processing">
              <i class="bx bx-receipt me-1"></i> Generate Top-Up Invoice (KES {{ formatMoney(totalAmount) }})
            </span>
            <span v-else>
              <span class="spinner-border spinner-border-sm me-2"></span>Processing…
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, trade, biller } from "@/api";

export default {
  name: "TopUpModal",
  props: {
    businessID: { type: String, default: "" },
    billNo:     { type: String, default: "" },
    feeID:      { type: String, default: "" }
  },
  emits: ["success"],
  data() {
    return {
      loading: false,
      processing: false,
      errorMessage: null,
      successMessage: null,

      // current state pulled from the bill
      businessName: '',
      currentBillAmount: 0,
      oldCategoryID: '',
      oldCategoryName: '',
      oldSubCategoryID: '',
      oldSubCategoryName: '',

      // new (correct) category
      categories: [],
      selectedCategory: null,
      fees: [],
      feesLoading: false,
      selectedFee: null,
      quantity: 1,
      reason: '',

      // extras
      extras: [],
      extraFeeOptions: [],  // pool of fees the officer can pick as add-ons

      // After a top-up bill is generated, hold the new billNo + the actual
      // balance the backend produced (which may differ from totalAmount if
      // penalties or other auto-charges were applied at bill-generation time).
      lastGeneratedBillNo: '',
      lastGeneratedBillAmount: 0,

      // Payment-prompt state on the success panel — lets officer send an
      // M-PESA STK push / SMS link to the customer for the just-generated bill.
      prompt: {
        phoneNumber: '',
        sending: false,
        success: false,
        message: ''
      },

      // Payment-check polling state. Same pattern NewBill.vue uses:
      // setInterval hits checkPayment every 3s for up to paymentTimeoutMs.
      paymentCheck: {
        active: false,
        timer: null,
        startedAt: 0,
        secondsRemaining: 0,
        receivedReceipt: null,    // populated when checkPayment finds a payment
        timedOut: false
      },
      paymentTimeoutMs: 90 * 1000   // 90s — covers normal STK push window
    };
  },
  computed: {
    newFeeAmount() {
      if (!this.selectedFee) return 0;
      const unit = parseFloat(this.selectedFee.unitFeeAmount) || 0;
      const qty = Math.max(1, parseInt(this.quantity) || 1);
      return unit * qty;
    },
    rawDifference() {
      return this.newFeeAmount - parseFloat(this.currentBillAmount || 0);
    },
    difference() {
      return Math.max(0, this.rawDifference);
    },
    extrasTotal() {
      return this.extras.reduce((sum, l) => sum + (parseFloat(l.amount) || 0) * (parseInt(l.quantity) || 1), 0);
    },
    totalAmount() {
      return this.difference + this.extrasTotal;
    },
    canSubmit() {
      return !!this.selectedCategory
          && !!this.selectedFee
          && !!this.reason && this.reason.trim().length > 0
          && this.totalAmount > 0
          && !this.processing;
    }
  },
  methods: {
    /** Called by the parent right after showing the modal. */
    initModal() {
      this.errorMessage = null;
      this.successMessage = null;
      this.processing = false;
      this.extras = [];
      this.selectedCategory = null;
      this.selectedFee = null;
      this.fees = [];
      this.quantity = 1;
      this.reason = '';
      this.lastGeneratedBillNo = '';
      this.lastGeneratedBillAmount = 0;
      this.prompt = { phoneNumber: '', sending: false, success: false, message: '' };
      this.stopPaymentCheck();
      this.paymentCheck.receivedReceipt = null;
      this.paymentCheck.timedOut = false;

      this.loadBusinessAndBill();
      this.loadSbpCategories();
      this.loadExtraFeeOptions();
    },

    /**
     * Open the standard bill print/preview page in a new tab. Same pattern
     * /bills-raised uses (printBill on BillsRaised.vue) — drops billNo into
     * localStorage and opens the named "bill" route. Officer can then
     * Save-as-PDF and share via WhatsApp / email.
     */
    printGeneratedBill() {
      if (!this.lastGeneratedBillNo) return;
      try {
        localStorage['params'] = JSON.stringify({ billNo: this.lastGeneratedBillNo });
        const routeData = this.$router.resolve({ name: 'bill' });
        window.open(routeData.href, '_blank');
      } catch (e) {
        console.error('printGeneratedBill failed', e);
        alert('Could not open the invoice for printing.');
      }
    },

    closeAfterPrint() {
      // Modal closes automatically (data-bs-dismiss="modal"). Reset so the
      // next opening starts fresh and any pending poll loop is cancelled.
      this.lastGeneratedBillNo = '';
      this.lastGeneratedBillAmount = 0;
      this.stopPaymentCheck();
    },

    // ==================== PAYMENT POLLING ====================
    // Mirrors NewBill.vue's startPaymentCheck/checkPayment/timeout pattern.
    // Once the STK push fires, hit checkPayment every 3s with the bill no.
    // When checkPayment returns success, payment landed — show the receipt
    // number and stop polling.
    startPaymentCheck() {
      this.stopPaymentCheck();
      this.paymentCheck.active = true;
      this.paymentCheck.startedAt = Date.now();
      this.paymentCheck.timedOut = false;
      this.paymentCheck.receivedReceipt = null;
      this.paymentCheck.secondsRemaining = Math.ceil(this.paymentTimeoutMs / 1000);
      // Tick immediately, then on interval.
      this.tickPaymentCheck();
      this.paymentCheck.timer = setInterval(() => this.tickPaymentCheck(), 3000);
    },
    tickPaymentCheck() {
      const elapsed = Date.now() - this.paymentCheck.startedAt;
      if (elapsed >= this.paymentTimeoutMs) {
        this.handlePaymentTimeout();
        return;
      }
      this.paymentCheck.secondsRemaining = Math.max(0,
          Math.ceil((this.paymentTimeoutMs - elapsed) / 1000));

      const data = new FormData();
      data.append('function', 'checkPayment');
      data.append('accountRef', this.lastGeneratedBillNo);
      execute(data, biller)
          .then((res) => {
            if (res.data && res.data.success) {
              this.handlePaymentSuccess(res.data);
            }
            // Otherwise keep polling silently — STK push hasn't completed yet.
          })
          .catch(() => { /* network blip — try again on next tick */ });
    },
    handlePaymentSuccess(payload) {
      this.stopPaymentCheck();
      const receipt = payload && payload.data && payload.data.receipt
          && payload.data.receipt.receiptDetails;
      this.paymentCheck.receivedReceipt = receipt || { receiptNo: 'PAID' };
      this.paymentCheck.timedOut = false;
      this.prompt.success = true;
      this.prompt.message = receipt && receipt.receiptNo
          ? `Payment received — receipt ${receipt.receiptNo}.`
          : 'Payment received.';
      // Refresh the live amount in case any post-payment server logic
      // changed billBalance (it should now be 0).
      this.refreshGeneratedBillAmount(this.lastGeneratedBillNo);
    },
    handlePaymentTimeout() {
      this.stopPaymentCheck();
      this.paymentCheck.timedOut = true;
    },
    stopPaymentCheck() {
      if (this.paymentCheck && this.paymentCheck.timer) {
        clearInterval(this.paymentCheck.timer);
        this.paymentCheck.timer = null;
      }
      if (this.paymentCheck) {
        this.paymentCheck.active = false;
      }
    },

    /**
     * Re-fetches the just-generated bill via getBill so the amount we show
     * (and send for payment) reflects whatever the backend actually wrote
     * to billdetails — including any penalties / auto-charges appended at
     * bill-generation time.
     */
    refreshGeneratedBillAmount(billNo) {
      if (!billNo) return;
      const data = new FormData();
      data.append('function', 'getBill');
      data.append('billNo', billNo);
      execute(data, biller)
          .then((res) => {
            if (!(res.data && res.data.success)) return;
            const bd = (res.data.data && res.data.data.billDetails) || {};
            const live = parseFloat(bd.billBalance || bd.detailAmount || 0);
            if (!isNaN(live) && live > 0) {
              this.lastGeneratedBillAmount = live;
            }
          })
          .catch(() => { /* silent — we already have a fallback amount */ });
    },

    /**
     * Send the customer an M-PESA STK push / SMS payment link for the
     * just-generated top-up bill. Reuses the same makePayment API as
     * /new-bill and the Look Up Invoice tab.
     */
    sendTopUpPaymentPrompt() {
      if (!this.lastGeneratedBillNo) return;
      const phone = (this.prompt.phoneNumber || '').trim();
      if (!phone) {
        this.prompt.success = false;
        this.prompt.message = 'Enter the customer phone number first.';
        return;
      }

      this.prompt.sending = true;
      this.prompt.success = false;
      this.prompt.message = '';

      // Always send the actual invoice amount — never our pre-calculation,
      // because the backend may have added penalties / auto-charges between
      // our submit and the bill being persisted.
      const liveAmount = parseFloat(this.lastGeneratedBillAmount) > 0
          ? parseFloat(this.lastGeneratedBillAmount)
          : parseFloat(this.totalAmount) || 0;

      const paymentData = {
        accNo: this.lastGeneratedBillNo,
        amount: liveAmount,
        payBillNo: '',
        phoneNumber: phone,
        paymentMode: 'MPESA',
        sessionPhoneNumber: sessionStorage.getItem('sessionPhoneNumber') || '',
        sessionNames: sessionStorage.getItem('sessionNames') || '',
        sessionIdNo: sessionStorage.getItem('sessionIdNo') || ''
      };

      const data = new FormData();
      data.append('function', 'makePayment');
      data.append('paymentData', JSON.stringify(paymentData));

      // Pick stream by bill prefix — same convention NewBill.vue uses.
      const prefix = String(this.lastGeneratedBillNo).toUpperCase();
      let stream = biller;
      if (prefix.startsWith('SBP')) stream = trade;

      execute(data, stream)
          .then((res) => {
            this.prompt.sending = false;
            if (res.data && res.data.success) {
              this.prompt.success = true;
              this.prompt.message = `Payment prompt for KES ${this.formatMoney(liveAmount)} sent to ${phone}. Ask the customer to check their phone and enter their M-PESA PIN.`;
              // Start polling for the actual payment to land.
              this.startPaymentCheck();
            } else {
              this.prompt.success = false;
              this.prompt.message = (res.data && res.data.message) || 'Could not send the payment prompt. Please try again.';
            }
          })
          .catch((e) => {
            this.prompt.sending = false;
            this.prompt.success = false;
            this.prompt.message = 'Network error sending prompt: ' + (e.message || 'unknown');
          });
    },

    formatMoney(v) {
      const n = parseFloat(v);
      if (isNaN(n)) return '0.00';
      return n.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },

    loadBusinessAndBill() {
      // Pull the current bill so we know what amount was already invoiced.
      // getBill returns billDetails (header) + billInfo (line items).
      if (!this.billNo) return;
      this.loading = true;
      const data = new FormData();
      data.append('function', 'getBill');
      data.append('billNo', this.billNo);
      execute(data, biller)
          .then((res) => {
            if (res.data && res.data.success) {
              const bd = res.data.data.billDetails || {};
              const bi = (res.data.data.billInfo || [])[0] || {};
              this.businessName       = bd.customerName || bd.names || '';
              this.currentBillAmount  = parseFloat(bd.detailAmount || bd.billBalance || 0);
              this.oldCategoryID      = bd.incomeTypeID || '';
              this.oldCategoryName    = bd.incomeTypeDescription || '';
              this.oldSubCategoryID   = bi.feeID || this.feeID || '';
              this.oldSubCategoryName = bi.feeDescription || bd.feeDescription || '';
              // Pre-fill payment-prompt phone from the original bill's customer
              if (!this.prompt.phoneNumber) {
                this.prompt.phoneNumber = bd.customerPhoneNumber || '';
              }
            }
            this.loading = false;
          })
          .catch((e) => {
            this.loading = false;
            this.errorMessage = 'Could not load current bill: ' + (e.message || 'unknown');
          });
    },

    loadSbpCategories() {
      const data = new FormData();
      data.append('function', 'getIncomeTypes');
      data.append('incomeTypePrefix', 'SBP');
      execute(data, biller)
          .then((res) => {
            if (res.data && res.data.success) {
              this.categories = res.data.data.incomeTypes || [];
            }
          })
          .catch(() => {});
    },

    onCategorySelected() {
      this.selectedFee = null;
      this.fees = [];
      if (!this.selectedCategory) return;
      this.feesLoading = true;
      const data = new FormData();
      data.append('function', 'getFeesAndCharges');
      data.append('incomeTypeId', this.selectedCategory.incomeTypeId);
      execute(data, biller)
          .then((res) => {
            if (res.data && res.data.success) {
              this.fees = res.data.data.feesAndCharges || [];
            }
            this.feesLoading = false;
          })
          .catch(() => { this.feesLoading = false; });
    },

    loadExtraFeeOptions() {
      // Mirrors the Android Enforcement-craft app's BusinessActivityInformation
      // pattern: pull SBP "Other" income types first, then for each one fetch
      // its fees and concat into a flat picker list. If "SBP + Other" returns
      // nothing, fall back to ALL income types (no prefix) — same fallback
      // the app uses when otherChargesFilter switches from "sbp_other" to "all".
      this.extraFeeOptions = [];
      this._loadOtherChargesByPrefix(true);
    },

    _loadOtherChargesByPrefix(useSbpOtherFilter) {
      const data = new FormData();
      data.append('function', 'getIncomeTypes');
      if (useSbpOtherFilter) {
        data.append('incomeTypePrefix', 'SBP');
        data.append('keyword', 'Other');
      }

      execute(data, biller)
          .then((res) => {
            const ok = res.data && res.data.success;
            const types = (ok && res.data.data && res.data.data.incomeTypes) || [];

            if (!types.length) {
              if (useSbpOtherFilter) {
                // Fallback: no SBP-Other matched, retry with ALL income types
                this._loadOtherChargesByPrefix(false);
              }
              return;
            }

            // For each income type, pull its fees in parallel and concatenate.
            const feePromises = types.map((t) => {
              const fd = new FormData();
              fd.append('function', 'getFeesAndCharges');
              fd.append('incomeTypeId', t.incomeTypeId);
              return execute(fd, biller)
                  .then((r) => {
                    if (!(r.data && r.data.success)) return [];
                    const list = (r.data.data && r.data.data.feesAndCharges) || [];
                    // Decorate each fee with the parent income-type label so
                    // the dropdown is unambiguous.
                    return list.map((f) => ({
                      ...f,
                      _incomeTypeId: t.incomeTypeId,
                      _incomeTypeDescription: t.incomeTypeDescription
                    }));
                  })
                  .catch(() => []);
            });

            Promise.all(feePromises).then((arrays) => {
              this.extraFeeOptions = arrays.reduce((acc, arr) => acc.concat(arr), []);
            });
          })
          .catch(() => {
            if (useSbpOtherFilter) this._loadOtherChargesByPrefix(false);
          });
    },

    addExtraCharge() {
      this.extras.push({ fee: null, description: '', quantity: 1, amount: 0, feeId: '', incomeTypePrefix: '' });
    },
    onExtraFeeChange(line) {
      if (!line.fee) return;
      line.amount = parseFloat(line.fee.unitFeeAmount) || 0;
      line.feeId = line.fee.feeId;
      line.incomeTypePrefix = line.fee.incomeTypePrefix || '';
      line.description = line.fee.feeDescription || '';
    },
    removeExtra(i) { this.extras.splice(i, 1); },

    /**
     * Generate the top-up invoice via generateBill3, then record the
     * pending category change. The countyTrade.businesses row is NOT
     * updated here — it gets updated when the bill is paid.
     */
    processTopUp() {
      if (!this.canSubmit) return;
      this.processing = true;
      this.errorMessage = null;
      this.successMessage = null;

      const billItem = [];
      // Difference line (only if positive)
      if (this.difference > 0) {
        const customer = `Business ID:${this.businessID} | Category Top-Up`;
        billItem.push({
          feeId: this.selectedFee.feeId,
          unitFeeAmount: this.selectedFee.unitFeeAmount,
          quantity: this.quantity,
          amount: this.newFeeAmount,
          customer: customer,
          description: `Category correction: ${this.oldCategoryName || '?'} / ${this.oldSubCategoryName || '?'} -> ${this.selectedCategory.incomeTypeDescription} / ${this.selectedFee.feeDescription}. ${this.reason}`,
          subCountyName: '',
          subCountyID: '',
          wardName: '',
          wardID: '',
          zone: '',
          idNo: sessionStorage.getItem('sessionIdNo') || ''
        });
      }
      // Extras
      this.extras.forEach((x) => {
        if (!x.amount || x.amount <= 0) return;
        billItem.push({
          feeId: x.feeId || '',
          unitFeeAmount: x.amount,
          quantity: x.quantity || 1,
          amount: (parseFloat(x.amount) || 0) * (parseInt(x.quantity) || 1),
          customer: `Business ID:${this.businessID} | Top-Up Extra`,
          description: x.description || 'Additional charge',
          subCountyName: '',
          subCountyID: '',
          wardName: '',
          wardID: '',
          zone: '',
          idNo: sessionStorage.getItem('sessionIdNo') || ''
        });
      });

      if (!billItem.length) {
        this.processing = false;
        this.errorMessage = 'Nothing to invoice — total is zero.';
        return;
      }

      const payload = new FormData();
      payload.append('function', 'generateBill3');
      payload.append('billItem', JSON.stringify(billItem));
      payload.append('payNow', 'false');
      payload.append('customerPhoneNumber', '');
      payload.append('email', '');

      execute(payload, biller)
          .then((res) => {
            if (!(res.data && res.data.success)) {
              this.processing = false;
              this.errorMessage = (res.data && res.data.message) || 'Could not generate the invoice.';
              return;
            }
            const respBill = (res.data.data && res.data.data.bill) || {};
            const respBillDetails = respBill.billDetails || {};
            const newBillNo = respBillDetails.billNo || '';

            // The actual invoice amount may include penalties / auto-charges
            // the backend appended on top of our calculated total. Trust the
            // response first, then re-fetch via getBill to be authoritative.
            const responseAmount = parseFloat(
                respBillDetails.billBalance ||
                respBillDetails.detailAmount ||
                this.totalAmount
            );

            // Store the pending category change tied to this new billNo.
            this.recordPendingChange(newBillNo).then(() => {
              this.lastGeneratedBillNo = newBillNo;
              this.lastGeneratedBillAmount = responseAmount;
              this.successMessage = `Top-up invoice ${newBillNo} generated. The business category will update once this invoice is paid.`;
              this.processing = false;
              this.refreshGeneratedBillAmount(newBillNo);
              this.$emit('success', {
                billNo: newBillNo,
                amount: responseAmount,
                businessID: this.businessID,
                newCategoryID: this.selectedCategory ? this.selectedCategory.incomeTypeId : '',
                newCategoryName: this.selectedCategory ? this.selectedCategory.incomeTypeDescription : '',
                newSubCategoryID: this.selectedFee ? this.selectedFee.feeId : '',
                newSubCategoryName: this.selectedFee ? this.selectedFee.feeDescription : ''
              });
            });
          })
          .catch((e) => {
            this.processing = false;
            this.errorMessage = 'Error generating invoice: ' + (e.message || 'unknown');
          });
    },

    recordPendingChange(newBillNo) {
      if (!newBillNo) return Promise.resolve();
      const data = new FormData();
      data.append('function', 'recordCategoryChange');
      data.append('businessID', this.businessID);
      data.append('billNo', newBillNo);
      data.append('oldCategoryID', this.oldCategoryID || '');
      data.append('oldCategoryName', this.oldCategoryName || '');
      data.append('oldSubCategoryID', this.oldSubCategoryID || '');
      data.append('oldSubCategoryName', this.oldSubCategoryName || '');
      data.append('newCategoryID', this.selectedCategory ? this.selectedCategory.incomeTypeId : '');
      data.append('newCategoryName', this.selectedCategory ? this.selectedCategory.incomeTypeDescription : '');
      data.append('newSubCategoryID', this.selectedFee ? this.selectedFee.feeId : '');
      data.append('newSubCategoryName', this.selectedFee ? this.selectedFee.feeDescription : '');
      data.append('newFeeID', this.selectedFee ? this.selectedFee.feeId : '');
      data.append('differenceAmount', this.difference);
      data.append('extraChargesAmount', this.extrasTotal);
      data.append('totalAmount', this.totalAmount);
      data.append('reason', this.reason || '');
      data.append('sessionNames', sessionStorage.getItem('sessionNames') || '');
      data.append('sessionIdNo', sessionStorage.getItem('sessionIdNo') || '');
      return execute(data, biller).catch((e) => {
        // Non-blocking — the bill itself was created. Surface a soft warning.
        console.error('recordCategoryChange failed', e);
      });
    }
  },

  beforeUnmount() {
    // Make sure the polling timer never leaks if the component is destroyed
    // while a payment-check loop is still running.
    this.stopPaymentCheck();
  }
};
</script>
